import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Card, CardContent, TextField, MenuItem, Button, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Alert, Chip, TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

interface Row {
  call_id: string; start_time: string; direction: string; src_number: string; dst_number: string;
  carrier: string; duration: number; billable_duration: number; status: string;
  hangup_cause: string; hangup_code: number; buy_rate: string; buy_cost: string; codec: string;
}

const STATUSES = ['', 'answered', 'noanswer', 'busy', 'failed', 'cancelled'];
const statusColor: Record<string, any> = { answered: 'success', busy: 'warning', noanswer: 'default', failed: 'error', cancelled: 'default' };
const fmtTime = (s: string) => new Date(s).toLocaleString();
const money = (n: any) => '$' + (parseFloat(n) || 0).toFixed(6);

// default range: last 60 days
const iso = (d: Date) => d.toISOString().slice(0, 10);
const today = new Date();
const twoMonthsAgo = new Date(Date.now() - 60 * 864e5);

export const BTCdrPage: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [from, setFrom] = useState(iso(twoMonthsAgo));
  const [to, setTo] = useState(iso(today));
  const [status, setStatus] = useState('');
  const [dst, setDst] = useState('');
  const [country, setCountry] = useState('');
  const [countryOpts, setCountryOpts] = useState<{ code: string; name: string; calls: number }[]>([]);

  useEffect(() => { api.get('/carrier-monitor/countries').then(r => setCountryOpts(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get('/carrier-monitor/cdrs', {
      params: { from, to, status: status || undefined, country: country || undefined, dst: dst || undefined, limit: rowsPerPage, offset: page * rowsPerPage },
    })
      .then(r => { setRows(r.data.rows); setTotal(r.data.total); })
      .catch(e => setErr(e?.response?.data?.message || 'Failed to load CDRs'))
      .finally(() => setLoading(false));
  }, [from, to, status, country, dst, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(0); load(); };

  const exportCsv = () => {
    const cols = ['start_time', 'direction', 'src_number', 'dst_number', 'carrier', 'duration', 'billable_duration', 'status', 'hangup_cause', 'hangup_code', 'buy_rate', 'buy_cost', 'codec'];
    const head = cols.join(',');
    const body = rows.map(r => cols.map(c => `"${String((r as any)[c] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([head + '\n' + body], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bt-cdr-${from}_to_${to}.csv`;
    a.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>CDR (raw)</Typography>
            <Typography variant="body2" color="text.secondary">BT call records — cost side only (last 2 months available)</Typography>
          </Box>
          <Chip color="primary" label={`Total calls: ${total.toLocaleString()}`} sx={{ fontWeight: 700 }} />
        </Box>
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} variant="outlined" size="small" sx={{ mr: 1 }} disabled={!rows.length}>Export CSV</Button>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined" size="small">Refresh</Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Status" select size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 130 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>)}
          </TextField>
          <TextField label="Country" select size="small" value={country} onChange={e => setCountry(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">All countries</MenuItem>
            {countryOpts.filter(c => c.code).map(c => <MenuItem key={c.code} value={c.code}>{c.name} ({c.calls.toLocaleString()})</MenuItem>)}
          </TextField>
          <TextField label="Destination starts with" size="small" value={dst} onChange={e => setDst(e.target.value)} placeholder="e.g. 44" />
          <Button variant="contained" onClick={applyFilters}>Apply</Button>
        </CardContent>
      </Card>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Dir</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Carrier</TableCell>
                    <TableCell align="right">Dur (s)</TableCell>
                    <TableCell align="right">Billable (s)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Cause</TableCell>
                    <TableCell align="right">Buy Rate</TableCell>
                    <TableCell align="right">Buy Cost</TableCell>
                    <TableCell>Codec</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.call_id + i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(r.start_time)}</TableCell>
                      <TableCell>{r.direction}</TableCell>
                      <TableCell>{r.src_number}</TableCell>
                      <TableCell>{r.dst_number}</TableCell>
                      <TableCell>{r.carrier}</TableCell>
                      <TableCell align="right">{r.duration}</TableCell>
                      <TableCell align="right">{r.billable_duration}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={statusColor[r.status] || 'default'} /></TableCell>
                      <TableCell>{r.hangup_cause || r.hangup_code || ''}</TableCell>
                      <TableCell align="right">{money(r.buy_rate)}</TableCell>
                      <TableCell align="right">{money(r.buy_cost)}</TableCell>
                      <TableCell>{r.codec}</TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5, color: 'text.secondary' }}>No records for the selected filters.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[50, 100, 250, 500]}
          />
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        BT carrier records only · buy cost = what we owe BT · customer pricing is not shown.
      </Typography>
    </Box>
  );
};
